/**
 * QuickAddFAB — quick-add sheet controller for calendar tab.
 * Opens from the day sheet header or prefilled timeline drag-create.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef, useId } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  Platform,
  Keyboard,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { createReminder, updateSettings } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import { useRemindersStore } from '../../stores/reminderStore';
import { useTheme, ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';
import TagPicker from '../common/TagPicker';
import { getTagColor, normalizeTagColorKeys } from '../../utils/tagColors';
import { useBackHandler } from '../../navigation/useBackHandler';

interface Props {
  selectedDate: string;
  bottomInset: number;
  onCreated: () => void;
  openSignal?: number;
  prefillTime?: { startMinutes: number; durationMinutes: number } | null;
  onPrefillConsumed?: () => void;
}

export default function QuickAddFAB({ selectedDate, bottomInset, onCreated, openSignal, prefillTime, onPrefillConsumed }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const { t, i18n } = useTranslation();
  const sheetId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [timeRange, setTimeRange] = useState<{ startMinutes: number; durationMinutes: number; label: string; startTime: string } | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const lastOpenSignalRef = useRef(openSignal);
  const defaultReminderTimeMinutes = useAuthStore((state) => state.user?.defaultReminderTimeMinutes ?? 540);
  const tagColors = useAuthStore((state) => state.user?.tagColors ?? null);
  const updateUser = useAuthStore((state) => state.updateUser);
  const reminders = useRemindersStore((state) => state.reminders);

  const keyboardHeight = useSharedValue(0);

  // Allow callers to pass tab-bar offset via bottomInset; cap at a sane upper bound
  const safeBottom = Math.min(bottomInset, 140);

  useEffect(() => {
    const show = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      (e) => { keyboardHeight.value = withTiming(e.endCoordinates.height, { duration: 250 }); }
    );
    const hide = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => { keyboardHeight.value = withTiming(0, { duration: 250 }); }
    );
    return () => { show.remove(); hide.remove(); };
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    paddingBottom: keyboardHeight.value,
  }));

  const dateLabel = DateTime.fromISO(selectedDate)
    .setLocale(i18n.language)
    .toLocaleString(DateTime.DATE_MED);

  const defaultTimeLabel = useMemo(() => {
    return DateTime.fromISO(selectedDate)
      .startOf('day')
      .plus({ minutes: defaultReminderTimeMinutes })
      .setLocale(i18n.language)
      .toLocaleString(DateTime.TIME_SIMPLE);
  }, [defaultReminderTimeMinutes, i18n.language, selectedDate]);

  const suggestedTags = useMemo(() => {
    const tags = new Set<string>();
    reminders.forEach((reminder) => {
      reminder.tags?.split(',').forEach((tag) => {
        const normalized = tag.trim().toLowerCase();
        if (normalized) tags.add(normalized);
      });
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [reminders]);

  const tagChoices = useMemo(() => {
    const tags = new Set<string>([...suggestedTags, ...selectedTags]);
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [selectedTags, suggestedTags]);

  const toggleSelectedTag = useCallback((tag: string) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) return;
    setSelectedTags((current) => (
      current.includes(normalized)
        ? current.filter((selectedTag) => selectedTag !== normalized)
        : [...current, normalized]
    ));
  }, []);

  const handleOpen = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setOpen(true);
    setText('');
    setTimeRange(null);
    setSelectedTags([]);
    setShowTagPicker(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setText('');
    setTimeRange(null);
    setSelectedTags([]);
    setShowTagPicker(false);
  }, []);

  useBackHandler({
    id: `quick-add-${sheetId}`,
    label: 'Quick add',
    priority: 900,
    enabled: open,
    onBack: () => {
      handleClose();
      return true;
    },
  });

  useEffect(() => {
    if (openSignal == null || openSignal === lastOpenSignalRef.current) return;
    lastOpenSignalRef.current = openSignal;
    handleOpen();
  }, [openSignal, handleOpen]);

  // Open programmatically when drag-to-create provides a time
  useEffect(() => {
    if (prefillTime) {
      const startDt = DateTime.fromISO(selectedDate).startOf('day').plus({ minutes: prefillTime.startMinutes });
      const endDt = startDt.plus({ minutes: prefillTime.durationMinutes });
      setTimeRange({
        startMinutes: prefillTime.startMinutes,
        durationMinutes: prefillTime.durationMinutes,
        label: `${startDt.toFormat('h:mm a')} \u2013 ${endDt.toFormat('h:mm a')}`,
        startTime: startDt.toFormat('h:mm a'),
      });
      setOpen(true);
      setText('');
      setSelectedTags([]);
      setShowTagPicker(false);
      onPrefillConsumed?.();
    }
  }, [prefillTime]);

  const handleTagsDone = useCallback(async (tags: string[], colorUpdates?: Record<string, string>) => {
    const normalizedTags = Array.from(new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean)));
    setSelectedTags(normalizedTags);
    setShowTagPicker(false);

    if (!colorUpdates || Object.keys(colorUpdates).length === 0) return;

    const previousTagColors = tagColors ? normalizeTagColorKeys(tagColors) : null;
    const mergedTagColors = { ...(previousTagColors || {}), ...normalizeTagColorKeys(colorUpdates) };

    try {
      const { user: serverUser } = await updateSettings({ tagColors: mergedTagColors });
      if (serverUser) {
        updateUser(serverUser);
      } else {
        updateUser({ tagColors: previousTagColors });
      }
    } catch (error) {
      console.warn('[QuickAddFAB] tag color save failed', error);
      updateUser({ tagColors: previousTagColors });
      Alert.alert(t('common.error', 'Error'), t('settings.tagColorsSaveFailed', 'Tag colors could not be saved.'));
    }
  }, [tagColors, t, updateUser]);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSending(true);
    try {
      // Build structured reminder directly — no NLP
      const startOfDay = DateTime.fromISO(selectedDate).startOf('day');
      const runAt = timeRange
        ? startOfDay.plus({ minutes: timeRange.startMinutes })
        : startOfDay.plus({ minutes: defaultReminderTimeMinutes });
      const dur = timeRange && timeRange.durationMinutes > 15 ? timeRange.durationMinutes : null;
      await createReminder({
        body: trimmed,
        runAtUtc: runAt.toUTC().toISO()!,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        durationMinutes: dur,
      });
      handleClose();
      onCreated();
    } catch {
      // Silently fail — user can retry
    } finally {
      setSending(false);
    }
  }, [text, sending, selectedDate, handleClose, onCreated, timeRange, defaultReminderTimeMinutes, selectedTags]);

  return (
    <>
      <Modal visible={open} transparent animationType="fade" onRequestClose={handleClose}>
        <Animated.View style={[s.modalWrap, animatedStyle]}>
          <Pressable style={s.backdrop} onPress={handleClose} />
          <View style={[s.sheet, { paddingBottom: Math.max(safeBottom, 16) }]}>
            <View style={s.sheetHeader}>
              <Text style={s.sheetTitle}>{t('calendar.quickAddTitle', 'Quick Add')}</Text>
              <Text style={s.sheetDate}>{dateLabel}</Text>
            </View>
            {timeRange && (
              <View style={s.timeBadge}>
                <Ionicons name="time-outline" size={13} color={colors.primary} />
                <Text style={s.timeBadgeText}>{timeRange.label}</Text>
              </View>
            )}
            {!timeRange && (
              <View style={s.defaultTimeTip}>
                <Ionicons name="information-circle-outline" size={15} color={colors.primary} />
                <Text style={s.defaultTimeTipText}>
                  {t('calendar.quickAddDefaultTimeTip', 'No time entered: this will be scheduled at {{time}}.', { time: defaultTimeLabel })}
                </Text>
              </View>
            )}
            <View style={s.tagSection}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.tagRow}>
                <TouchableOpacity
                  style={s.addTagButton}
                  onPress={() => setShowTagPicker(true)}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel={t('calendar.addTag', 'Add tag')}
                >
                  <Ionicons name="add" size={18} color="#FFFFFF" />
                  <Text style={s.addTagButtonText}>{t('calendar.addTag', 'Add tag')}</Text>
                </TouchableOpacity>
                {tagChoices.map((tag) => {
                    const color = getTagColor(tag, tagColors);
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <TouchableOpacity
                        key={tag}
                        style={[
                          s.tagChoice,
                          { borderColor: isSelected ? color : color + '40', backgroundColor: isSelected ? color + '24' : color + '10' },
                        ]}
                        onPress={() => toggleSelectedTag(tag)}
                        activeOpacity={0.75}
                        accessibilityRole="button"
                        accessibilityLabel={t('calendar.toggleTag', 'Toggle {{tag}} tag', { tag })}
                        accessibilityState={{ selected: isSelected }}
                      >
                        <View style={[s.tagChoiceDot, { backgroundColor: color }]} />
                        <Text style={[s.tagChoiceText, { color: isSelected ? color : colors.textSecondary }]} numberOfLines={1}>{tag}</Text>
                        {isSelected ? <Ionicons name="checkmark" size={13} color={color} /> : null}
                      </TouchableOpacity>
                    );
                  })}
              </ScrollView>
            </View>
            <View style={s.inputRow}>
              <TextInput
                style={s.input}
                placeholder={t('calendar.quickAddPlaceholder', 'Reminder title')}
                placeholderTextColor="#94A3B8"
                value={text}
                onChangeText={setText}
                autoFocus
                returnKeyType="send"
                onSubmitEditing={handleSend}
                editable={!sending}
              />
              <TouchableOpacity
                style={[s.sendBtn, (!text.trim() || sending) && s.sendBtnDisabled]}
                onPress={handleSend}
                disabled={!text.trim() || sending}
                activeOpacity={0.7}
              >
                {sending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Ionicons name="arrow-up" size={20} color="#fff" />
                )}
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>
      </Modal>
      <TagPicker
        visible={showTagPicker}
        tags={selectedTags}
        tagColors={tagColors}
        suggestedTags={suggestedTags}
        onDone={handleTagsDone}
        onCancel={() => setShowTagPicker(false)}
      />
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  modalWrap: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 23, 42, 0.28)',
  },
  sheet: {
    backgroundColor: colors.card,
    borderTopLeftRadius: RADIUS.xl,
    borderTopRightRadius: RADIUS.xl,
    paddingHorizontal: SPACING.lg,
    paddingTop: SPACING.lg,
    ...(Platform.OS === 'ios' ? {
      ...SHADOW.elevated,
      shadowOffset: { width: 0, height: -4 },
    } : { elevation: 12 }),
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.base,
  },
  sheetTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  sheetDate: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    marginBottom: SPACING.md,
  },
  input: {
    flex: 1,
    height: SPACING['4xl'],
    borderRadius: RADIUS.lg,
    backgroundColor: colors.inputBackground,
    paddingHorizontal: SPACING.base,
    fontSize: FONT_SIZE.base,
    color: colors.text,
  },
  sendBtn: {
    width: SPACING['4xl'],
    height: SPACING['4xl'],
    borderRadius: RADIUS.xl,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendBtnDisabled: {
    opacity: 0.4,
  },
  timeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.primary + '12',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.md,
    marginBottom: SPACING.md,
    alignSelf: 'flex-start',
  },
  timeBadgeText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.primary,
  },
  defaultTimeTip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: colors.primary + '10',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.primary + '24',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.md,
    marginBottom: SPACING.md,
  },
  defaultTimeTipText: {
    flex: 1,
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  tagSection: {
    marginBottom: SPACING.base,
  },
  tagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingRight: SPACING.xs,
  },
  addTagButton: {
    height: 34,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primary,
    paddingHorizontal: SPACING.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  addTagButtonText: {
    color: '#FFFFFF',
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
  },
  tagChoice: {
    maxWidth: 140,
    height: 34,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: RADIUS.full,
    paddingHorizontal: SPACING.sm,
  },
  tagChoiceDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  tagChoiceText: {
    maxWidth: 92,
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
  },
});
